import { ReactElement, useState } from 'react'
import Image from 'next/image'
import { Alert, Placeholder } from 'react-bootstrap'
import { handleAction } from '@utils/router'
import { uniqueId } from '@utils/misc'
import { sortArrayOfObjects } from '@utils/objects'

/**
 * Type representing an alert.
 */
export type AlertType = "gray" | "info" | "brand" | "warning" | "error" | "danger" | "success";

export interface AlertProps {
  key?: string;
  title?: string;
  message?: string;
  icon?: any;
  link?: string;
  type: AlertType;
  dismissible?: boolean;
  priority?: number;
}

/**
 * Type representing a notification (extends AlertProps).
 */
export interface Notification extends Omit<AlertProps, 'type'> {
  key: string;
  variant: AlertType;
  content: string;
}

/**
 * Props for the Alerts component.
 */
interface AlertsProps {
  alerts?: AlertProps[];
  loaded?: boolean;
}

export function AlertsItemPlaceholder(): ReactElement {
  return (
    <Placeholder as={Alert} animation="wave" className="alert-placeholder">
      <Placeholder as={'h2'} xs={4}>
        <Placeholder />
      </Placeholder>
      <Placeholder as={'div'} xs={12}>
        <Placeholder as="p">
          <Placeholder />
        </Placeholder>
      </Placeholder>
    </Placeholder>
  );
}

/**
 * Component to display a list of alerts.
 */
export default function Alerts({ alerts = [], loaded = true }: AlertsProps): ReactElement {
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  // Convert alerts to notifications and sort by priority
  const notifications = sortArrayOfObjects<Notification>(
    alerts.map((alert) => ({
      key: alert.key || `alert-${ uniqueId() }`,
      icon: alert.icon || null,
      title: alert.title || '',
      content: alert.message || '',
      link: alert.link || '',
      variant: alert.type || "warning",
      dismissible: alert.dismissible || false,
      priority: alert.priority || 0,
    })),
    "priority"
  ).reverse();

  const closeAlert = (alert: Notification) => {
    if (!alert.dismissible) return;
    setDismissed((prev) => ({ ...prev, [alert.key]: true }));
  };

  return (
    <>
      {! loaded && notifications.length === 0 && <AlertsItemPlaceholder /> }
      {notifications.map(
        (notification) => {
          if (dismissed[notification.key]) {
            return;
          }

          const icon = notification?.icon || null;

          const classLink = notification.link ? 'cursor-pointer' : '';
          const classIcon = icon ? 'has-icon' : '';

          const onClick = () => {
            if (! notification?.link) {
              return;
            }

            handleAction({ action: notification.link, target: '_blank' });
          }

          return (
            <Alert
              key={notification.key}
              variant={notification.variant}
              dismissible={notification.dismissible}
              onClose={(event) => closeAlert(notification)}
              className={`${classLink} ${classIcon}`}
            >
              <div className="d-flex flex-grow-1" onClick={onClick}>
                { icon && <Image {...icon} /> }
                <div className="text-start alert-content-wrapper">
                  {notification.title && (
                    <h2
                      className="alert-title"
                      dangerouslySetInnerHTML={{ __html: notification.title }}
                    />
                  )}
                  {notification.content && (
                    <div
                      className="alert-content"
                      dangerouslySetInnerHTML={{ __html: notification.content.replace("\n", '<br>') }}
                    />
                  )}
                </div>
              </div>
            </Alert>
          )
        }
      )}
    </>
  );
};
